import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useMutation, gql } from "@apollo/client";
import { format } from "date-fns";
import { Stethoscope } from "lucide-react";
import { SectionHeader } from "./SectionHeader";
import { toast } from "@/components/ui/toast";

const ADD_COORDINATED_CARE_NOTE = gql`
  mutation AddCoordinatedCareNote($odrnumber: String!, $participant: String!, $note: AddCoordinatedCareNoteInput!) {
    addCoordinatedCareNote(odrnumber: $odrnumber, participant: $participant, note: $note) {
      message
    }
  }
`;

// Service Type options
const SERVICE_TYPE_OPTIONS = [
  { value: "none", label: "-- Select type --" },
  { value: "Housing", label: "Housing" },
  { value: "Community", label: "Community" },
  { value: "Pre-release", label: "Pre-release" },
  { value: "Psychiatry", label: "Psychiatry" },
  { value: "Nursing", label: "Nursing" }
];

// Place of Service options
const PLACE_OF_SERVICE_OPTIONS = [
  { value: "none", label: "-- Select an option --" },
  { value: "home", label: "Home" },
  { value: "office", label: "Office" },
  { value: "telehealth_home", label: "Telehealth in pt home" },
  { value: "telehealth_outside", label: "Telehealth outside pt home" }
];

const PROCEDURE_LENGTH_OPTIONS = [
  { value: "15", label: "15 mins" },
  { value: "30", label: "30 mins" },
  { value: "45", label: "45 mins" },
  { value: "60", label: "60 mins" },
  { value: "75", label: "1hour 15mins" },
  { value: "90", label: "1hour 30mins" },
  { value: "105", label: "1hour 45mins" },
  { value: "120", label: "2hours" },
  { value: "150", label: "2hours 30mins" },
  { value: "180", label: "3hours" },
  { value: "210", label: "3hours 30mins" },
  { value: "240", label: "4hours" }
];

interface CoordinatedCareEntryFormProps {
  odrNumber: string;
  participantId: string;
  user: any;
  showHeader?: boolean;
  onSuccess: () => void;
  onCancel: () => void;
}

export const CoordinatedCareEntryForm: React.FC<CoordinatedCareEntryFormProps> = ({
  odrNumber,
  participantId,
  user,
  showHeader = true,
  onSuccess,
  onCancel
}) => {
  const [formData, setFormData] = useState({
    service_type: "",
    place_of_service: "",
    procedurelength: "15",
    note_entry_date: format(new Date(), "yyyy-MM-dd"),
    procedurenote: "",
    procedureentery: user?.realname || user?.fullname || user?.name || user?.username || ""
  });

  const [addCoordinatedCareNote, { loading }] = useMutation(ADD_COORDINATED_CARE_NOTE);
  const [errors, setErrors] = useState<{[key: string]: string}>({});

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name: string, value: string) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const validateForm = () => {
    const newErrors: {[key: string]: string} = {};

    if (!formData.place_of_service) {
      newErrors.place_of_service = "Place of Service must be selected";
    }

    if (!formData.procedurelength) {
      newErrors.procedurelength = "Procedure length must be selected";
    }

    if (!formData.procedurenote.trim()) {
      newErrors.procedurenote = "Procedure note is required";
    }

    if (!formData.procedureentery) {
      newErrors.procedureentery = "Entered by must be filled";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      await addCoordinatedCareNote({
        variables: {
          odrnumber: odrNumber,
          participant: participantId,
          note: {
            service_type: formData.service_type,
            place_of_service: formData.place_of_service,
            procedurelength: formData.procedurelength,
            note_entry_date: formData.note_entry_date,
            procedurenote: formData.procedurenote,
            procedureentery: formData.procedureentery
          },
        },
        refetchQueries: ["GetCoordinatedCareNotes", "GetSummaryData"],
        awaitRefetchQueries: true,
      });
      onSuccess();
    } catch (error) {
      console.error("Error adding Coordinate Care note:", error);
      if (error instanceof Error) {
        toast.error(error.message || "Failed to save coordinated care note");
        return;
      }
      toast.error("Failed to save coordinated care note");
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {showHeader && (
        <SectionHeader
          icon={Stethoscope}
          title="Coordinated Care Note"
          subtitle="Capture care coordination activity"
        />
      )}
      <div className="grid grid-cols-3 gap-4">
        <div className="space-y-2">
          <Label htmlFor="service_type">Service Type</Label>
          <Select 
            onValueChange={(value) => handleSelectChange("service_type", value)}
            value={formData.service_type}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select service type" />
            </SelectTrigger>
            <SelectContent>
              {SERVICE_TYPE_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="place_of_service">Place of Service</Label>
          <Select 
            onValueChange={(value) => handleSelectChange("place_of_service", value)}
            value={formData.place_of_service}
          >
            <SelectTrigger className={errors.place_of_service ? "border-red-500" : ""}>
              <SelectValue placeholder="Select place of service" />
            </SelectTrigger>
            <SelectContent>
              {PLACE_OF_SERVICE_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.place_of_service && <p className="text-sm text-red-500">{errors.place_of_service}</p>}
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="procedurelength">Procedure Length</Label>
          <Select
            onValueChange={(value) => handleSelectChange("procedurelength", value)}
            value={formData.procedurelength}
          >
            <SelectTrigger className={errors.procedurelength ? "border-red-500" : ""}>
              <SelectValue placeholder="Select procedure length" />
            </SelectTrigger>
            <SelectContent>
              {PROCEDURE_LENGTH_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.procedurelength && <p className="text-sm text-red-500">{errors.procedurelength}</p>}
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="procedureentery">Entered By</Label>
        <Input
          id="procedureentery"
          name="procedureentery"
          value={formData.procedureentery}
          className="bg-gray-100"
          readOnly
          required
        />
        {errors.procedureentery && <p className="text-sm text-red-500">{errors.procedureentery}</p>}
      </div>

      <div className="space-y-2">
        <Label htmlFor="procedurenote">Procedure Note</Label>
        <Textarea
          id="procedurenote"
          name="procedurenote"
          value={formData.procedurenote}
          onChange={handleInputChange}
          placeholder="Enter your note here"
          className={`min-h-[100px] ${errors.procedurenote ? "border-red-500" : ""}`}
          required
        />
        {errors.procedurenote && <p className="text-sm text-red-500">{errors.procedurenote}</p>}
      </div>

      <div className="flex items-end justify-between space-x-2 mt-6 pt-4 border-t">
        <div className="space-y-2 w-1/3">
          <Label htmlFor="note_entry_date">Note Date</Label>
          <Input
            type="date"
            id="note_entry_date"
            name="note_entry_date"
            value={formData.note_entry_date}
            onChange={handleInputChange}
            required
          />
        </div>

        <div className="flex space-x-2">
            <Button size="sm" type="submit" disabled={loading}>
            {loading ? "Saving..." : "Save Note"}
            </Button>
            <Button size="sm" variant="outline" onClick={onCancel} type="button">
            Cancel
            </Button>
        </div>
      </div>
    </form>
  );
};
